import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Icon from './Icon'
import Logo from './Logo'
import useSectionNav from '../hooks/useSectionNav'
import { APP_LINKS, HOME_SECTIONS } from '../siteConfig'

const Header = () => {
    const headerRef = useRef(null)
    const barRef = useRef(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const goToSection = useSectionNav()
    const { pathname } = useLocation()

    useEffect(() => { setMenuOpen(false) }, [pathname])

    // Scroll progress bar. Written straight to the DOM inside one rAF per frame
    // so scrolling never re-renders React. Page height is cached and only
    // re-measured on resize, so the handler never forces a layout.
    useEffect(() => {
        const header = headerRef.current
        const bar = barRef.current
        let maxScroll = 1
        let ticking = false

        const measure = () => {
            maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
        }
        const update = () => {
            const y = window.scrollY
            bar.style.transform = `scaleX(${Math.min(1, y / maxScroll)})`
            header.classList.toggle('scrolled', y > 20)
            ticking = false
        }
        const onScroll = () => {
            if (!ticking) {
                ticking = true
                requestAnimationFrame(update)
            }
        }

        const resizeObserver = new ResizeObserver(measure)
        resizeObserver.observe(document.body)
        measure()
        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', onScroll)
            resizeObserver.disconnect()
        }
    }, [])

    const handleSection = (e, id) => {
        e.preventDefault()
        setMenuOpen(false)
        goToSection(id)
    }

    const sectionLinks = HOME_SECTIONS.map(({ id, label }) => (
        <a key={id} href="#/" onClick={(e) => handleSection(e, id)}>{label}</a>
    ))

    return (
        <>
            <div className="scroll-bar" ref={barRef} aria-hidden="true" />
            <header className="site-header" ref={headerRef}>
                <div className="wrap nav-row">
                    <Link to="/" className="logo" aria-label="Poultryfy home" onClick={(e) => handleSection(e, 'home')}>
                        <Logo />
                    </Link>
                    <nav className="main-nav" aria-label="Main">
                        <ul>
                            {sectionLinks.map((link) => <li key={link.key}>{link}</li>)}
                        </ul>
                    </nav>
                    <div className="nav-actions">
                        <a href={APP_LINKS.userWebLogin} className="btn btn-ghost btn-sm">Login / Sign Up</a>
                        <a href="#/" className="btn btn-ghost btn-sm" onClick={(e) => handleSection(e, 'contact')}>Contact Us</a>
                        <a href="#/" className="btn btn-primary btn-sm" onClick={(e) => handleSection(e, 'download')}>Download App</a>
                        <button
                            type="button"
                            className="nav-toggle"
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            <Icon name={menuOpen ? 'x' : 'menu'} />
                        </button>
                    </div>
                </div>
                <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
                    <div className="wrap">
                        {sectionLinks}
                        <a href={APP_LINKS.userWebLogin}>Login / Sign Up</a>
                        {/* the header's Contact Us / Download buttons are hidden at this width */}
                        <a href="#/" onClick={(e) => handleSection(e, 'contact')}>Contact Us</a>
                        <a href="#/" onClick={(e) => handleSection(e, 'download')}>Download App</a>
                    </div>
                </div>
            </header>
        </>
    )
}

export default Header
